import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatCurrency } from '../utils/helpers';
import { clsx } from 'clsx';

export const Projects = () => {
  const { data } = useApp();
  const { projects } = data;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Project Registry</h1>
          <p className="text-sm text-slate-500">Official ledger of all NDP6-mapped government projects.</p>
        </div>
        <button className="bg-grn-navy text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-900 flex items-center gap-2 shadow-lg shadow-grn-navy/20 transition-all">
          <SafeIcon icon={FiIcons.FiPlus} /> Register Project
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <SafeIcon icon={FiIcons.FiSearch} className="absolute left-3 top-2.5 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search by ID, Name or Contractor..." 
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-grn-gold/50" 
          />
        </div>
        <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white cursor-pointer hover:border-slate-400">
          <option>All Ministries</option>
          <option>Works & Transport</option>
          <option>Education</option>
        </select>
        <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white cursor-pointer hover:border-slate-400">
          <option>All NDP6 Pillars</option>
          <option>Economic Advancement</option>
          <option>Social Transformation</option>
        </select>
        <button className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white hover:bg-slate-50 flex items-center justify-center gap-2 transition-colors">
          <SafeIcon icon={FiIcons.FiFilter} /> Advanced Filtering
        </button>
      </div>

      <Card className="overflow-hidden p-0 border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold tracking-widest border-b border-slate-200">
              <tr>
                <th className="px-6 py-4">Project ID & Name</th>
                <th className="px-6 py-4">Responsible OMA</th>
                <th className="px-6 py-4">Progress / Milestone</th>
                <th className="px-6 py-4">Contract Value</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {projects.map(project => (
                <tr key={project.id} className="hover:bg-blue-50/30 transition-colors group">
                  <td className="px-6 py-4">
                    <Link to={`/projects/${project.id}`} className="block group-hover:text-grn-navy transition-colors">
                      <div className="font-bold text-slate-900 leading-tight">{project.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono mt-1 opacity-70">{project.id}</div>
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-slate-600 font-medium max-w-[200px] truncate">{project.ministry}</div>
                    <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-tighter">{project.pillar}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-20 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                        <div 
                          className={clsx(
                            "h-1.5 rounded-full transition-all duration-1000",
                            project.status === 'Delayed' ? 'bg-red-500' : 'bg-grn-navy'
                          )}
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-xs font-black text-slate-700">{project.progress}%</span>
                    </div>
                    <div className="text-[10px] text-slate-500 truncate italic">{project.milestone}</div>
                  </td>
                  <td className="px-6 py-4 font-mono font-bold text-slate-700">
                    {formatCurrency(project.value)}
                  </td>
                  <td className="px-6 py-4">
                    <Badge status={project.status} size="sm" />
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link to={`/projects/${project.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded text-[11px] font-bold text-slate-600 hover:border-grn-navy hover:text-grn-navy transition-all" >
                      DETAILED VIEW
                      <SafeIcon icon={FiIcons.FiArrowRight} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-between items-center text-[11px] text-slate-500">
          <div className="flex items-center gap-4">
            <span>Showing <span className="font-bold text-slate-900">5</span> of <span className="font-bold text-slate-900">142</span> records</span>
            <div className="h-4 w-px bg-slate-300"></div>
            <span>Export to: <button className="text-grn-navy font-bold hover:underline ml-1">CSV</button>,<button className="text-grn-navy font-bold hover:underline ml-1">PDF</button></span>
          </div>
          <div className="flex gap-2">
            <button className="px-3 py-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-50 font-bold">PREV</button>
            <button className="px-3 py-1 border border-slate-300 rounded bg-white hover:bg-slate-50 font-bold">NEXT</button>
          </div>
        </div>
      </Card>
    </div>
  );
};